import {
  Box,
  Combobox as ChakraCombobox,
  ComboboxRootProps,
  Field,
  InputGroup,
  Portal,
  Text,
  createListCollection,
} from '@chakra-ui/react'
import {useQuery} from '@rocicorp/zero/react'
import {useTranslate} from '@tolgee/react'
import {useEffect, useMemo, useRef, useState} from 'react'
import {LuSearch} from 'react-icons/lu'
import {FieldRequiredIndicator} from '../../components/form/field-required-indicator.js'
import {BaseRowData} from '../../types/index.js'
import {type GetPageQuery, type GetSingleQuery, useZeroVirtualizer} from '../../utils/zero-virtualizer/index.js'
import {ErrorMessage} from './error-message.js'
import {FieldRoot} from './field-root.js'
import {SelectItem} from './select.js'
import {buildSelectionLabel} from './selection-label.js'
import {useSelectField} from './use-select-field.js'

interface Props<TRow extends BaseRowData, TStartRow, TListContextParams> extends Omit<ComboboxRootProps, 'collection'> {
  transformToSelectItem: (row: TRow) => SelectItem
  listHeight?: number
  estimateItemHeight?: number

  getPageQuery: GetPageQuery<TRow, TStartRow>
  getSingleQuery: GetSingleQuery<TRow>
  toStartRow: (row: TRow) => TStartRow
  listContextParams: TListContextParams
  /** Options not backed by a row (e.g. a "none" filter value), listed above the paginated rows. */
  staticItems?: SelectItem[]

  label: string
  hideLabel?: boolean
  /** Hides the leading magnifying glass on the control (the search still works). */
  hideSearchIcon?: boolean
  helpText?: string
  description?: string
  placeholder?: string
  search?: string
  onSearchChange?: (value: string) => void
  controlProps?: ChakraCombobox.ControlProps
  contentProps?: ChakraCombobox.ContentProps
  portalled?: boolean
  displayClearTrigger?: boolean
  errorMessagePadding?: number
}

export function InfiniteCombobox<
  TRow extends BaseRowData,
  TStartRow extends BaseRowData,
  TListContextParams extends {limit: number},
>({
  transformToSelectItem,
  listHeight = 20 * 16,
  estimateItemHeight = 32,
  getPageQuery,
  getSingleQuery,
  toStartRow,
  listContextParams,
  staticItems,
  label,
  hideLabel,
  hideSearchIcon,
  children,
  controlProps,
  contentProps,
  description,
  helpText,
  displayClearTrigger = true,
  errorMessagePadding,
  placeholder,
  search,
  portalled = true,
  required,
  ...props
}: Props<TRow, TStartRow, TListContextParams>) {
  const {t} = useTranslate('common')
  const {field, value, handleChange} = useSelectField()
  const scrollRef = useRef<HTMLDivElement>(null)

  const {virtualizer, rowAt, complete, rowsEmpty} = useZeroVirtualizer<
    HTMLDivElement,
    Element,
    TListContextParams,
    TRow,
    TStartRow
  >({
    estimateSize: () => estimateItemHeight,
    overscan: 5,
    getScrollElement: () => scrollRef.current,
    listContextParams,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    getRowKey: row => row.id ?? '',
  })

  const virtualItems = virtualizer.getVirtualItems()

  const [inputValue, setInputValue] = useState(search || undefined)
  const selectedItems = useRef<SelectItem[]>([])

  const firstQuery = useMemo(() => {
    if (value.length === 0) return null
    return getSingleQuery({id: value[0], settled: true})
  }, [value, getSingleQuery])

  const [firstRowData] = useQuery(firstQuery?.query ?? null, firstQuery?.options)
  const firstRow = firstRowData as TRow | undefined

  const firstItem = useMemo(() => {
    if (!firstRow?.id) return undefined
    return transformToSelectItem(firstRow)
  }, [firstRow, transformToSelectItem])

  useEffect(() => {
    if (!firstItem || inputValue !== undefined) return
    selectedItems.current = [firstItem]
    setInputValue(buildSelectionLabel(firstItem.label, value.length - 1, t('more')))
  }, [firstItem, inputValue, value, t])

  const {collection, rowToItem} = useMemo(() => {
    const itemMap = new Map<string, SelectItem>()

    for (const item of staticItems ?? []) {
      itemMap.set(item.value, item)
    }

    if (firstItem) {
      itemMap.set(firstItem.value, firstItem)
    }

    for (const vItem of virtualItems) {
      const row = rowAt(vItem.index)
      if (row?.id) {
        const item = transformToSelectItem(row)
        itemMap.set(row.id, item)
      }
    }

    const collectionItems = Array.from(itemMap.values())

    return {
      collection: createListCollection({items: collectionItems}),
      rowToItem: (row: TRow | undefined) => {
        if (!row?.id) return undefined
        return itemMap.get(row.id)
      },
    }
  }, [virtualItems, rowAt, transformToSelectItem, firstItem, staticItems])

  function autoSetInputValue() {
    setInputValue(buildSelectionLabel(selectedItems.current[0]?.label, selectedItems.current.length - 1, t('more')))
  }

  const handleValueChange = (e: ChakraCombobox.ValueChangeDetails) => {
    selectedItems.current = e.items
    autoSetInputValue()

    if (e.value.length === 0) {
      handleChange([])
      return
    }

    if (e.value[0] === 'EMPTY_STATE') return

    handleChange(e.value)
  }

  const onSearchChange: typeof props.onSearchChange = value => {
    if (value) setInputValue(value)
    props.onSearchChange?.(value)
  }

  const onOpenChange: typeof props.onOpenChange = details => {
    if (details.open) {
      props?.onSearchChange?.('')
    } else {
      autoSetInputValue()
    }

    props.onOpenChange?.(details)
  }

  return (
    <FieldRoot required={required} disabled={props.disabled}>
      <ChakraCombobox.Root
        width="100%"
        collection={collection}
        name={field.name}
        value={value}
        disabled={props.disabled}
        onValueChange={handleValueChange}
        inputValue={inputValue}
        onBlur={field.handleBlur}
        openOnClick
        {...props}
        positioning={{sameWidth: true, ...props.positioning}}
        onInputValueChange={e => onSearchChange?.(e.inputValue)}
        onOpenChange={onOpenChange}
      >
        <Field.Label asChild>
          <ChakraCombobox.Label srOnly={hideLabel}>
            {label} {required && <FieldRequiredIndicator />}
          </ChakraCombobox.Label>
        </Field.Label>

        {helpText && <Field.HelperText srOnly={hideLabel}>{helpText}</Field.HelperText>}

        {!children && (
          <ChakraCombobox.Control backgroundColor="bg" {...controlProps}>
            <InputGroup startElement={hideSearchIcon ? undefined : <LuSearch />} title={inputValue}>
              <ChakraCombobox.Input placeholder={placeholder} disabled={props.disabled} />
            </InputGroup>
            <ChakraCombobox.IndicatorGroup>
              {displayClearTrigger && <ChakraCombobox.ClearTrigger />}
              <ChakraCombobox.Trigger />
            </ChakraCombobox.IndicatorGroup>
          </ChakraCombobox.Control>
        )}

        {description && (
          <Field.HelperText mt={2} srOnly={description}>
            {description}
          </Field.HelperText>
        )}

        {children && (
          <ChakraCombobox.Control {...controlProps}>
            <ChakraCombobox.Trigger asChild unstyled>
              {children}
            </ChakraCombobox.Trigger>
          </ChakraCombobox.Control>
        )}

        <Portal disabled={!portalled}>
          <ChakraCombobox.Positioner width="100%">
            <ChakraCombobox.Content minWidth="20rem" overflow="auto" opacity="1" {...contentProps}>
              {children && (
                <Box position="sticky" top="0" left="0" bg="bg" zIndex="1">
                  <InputGroup startElement={<LuSearch />} position="relative">
                    <ChakraCombobox.Input value={search} pl="8!" h="6" />
                  </InputGroup>
                  <ChakraCombobox.IndicatorGroup>
                    <ChakraCombobox.ClearTrigger />
                  </ChakraCombobox.IndicatorGroup>
                </Box>
              )}

              <Box ref={scrollRef} overflow="auto" maxHeight={`${listHeight}px`}>
                {staticItems?.map(item => (
                  <ChakraCombobox.Item key={item.value} item={item} truncate height={`${estimateItemHeight}px`}>
                    {item.label}
                    <ChakraCombobox.ItemIndicator />
                  </ChakraCombobox.Item>
                ))}

                {rowsEmpty && complete ? (
                  <Text padding="1" fontSize="xs" color="fg.muted">
                    {t('no_options_available')}
                  </Text>
                ) : (
                  <>
                    <div style={{height: `${virtualizer.getTotalSize()}px`, position: 'relative'}}>
                      {virtualItems.map(virtualRow => {
                        const row = rowAt(virtualRow.index)
                        const item = rowToItem(row)

                        if (!item) {
                          return null
                        }

                        return (
                          <div
                            key={virtualRow.key}
                            data-index={virtualRow.index}
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: `${estimateItemHeight}px`,
                              transform: `translateY(${virtualRow.start}px)`,
                            }}
                            title={item.label}
                          >
                            <ChakraCombobox.Item item={item} truncate height="100%">
                              {item.label}
                              <ChakraCombobox.ItemIndicator />
                            </ChakraCombobox.Item>
                          </div>
                        )
                      })}
                    </div>
                  </>
                )}

                {!complete && (
                  <Text padding="1" fontSize="xs" color="fg.muted">
                    {t('loading')}
                  </Text>
                )}
              </Box>
            </ChakraCombobox.Content>
          </ChakraCombobox.Positioner>
        </Portal>
      </ChakraCombobox.Root>

      <ErrorMessage errorMessagePadding={errorMessagePadding} />
    </FieldRoot>
  )
}
